import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatDate } from "@/lib/api";
import SEO from "@/components/SEO";
import { FadeUp, Eyebrow, usePageTitle } from "@/components/Motion";

export default function Journal() {
  usePageTitle("Journal");
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/journal").then((r) => setPosts(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const cats = useMemo(() => ["all", ...new Set(posts.map((p) => p.category))], [posts]);
  const filtered = cat === "all" ? posts : posts.filter((p) => p.category === cat);
  const [first, ...rest] = filtered;

  return (
    <div className="pt-16" data-testid="journal-page">
      <SEO title="Film Production Journal & Case Studies"
  description="Read production stories, case studies, filmmaking insights and behind-the-scenes updates from Incolour Films in Toronto."
  path="/journal"
        />
      <section className="mx-auto max-w-7xl px-4 pb-10 pt-16 sm:px-6 lg:px-8 lg:pt-24">
        <Eyebrow>Journal</Eyebrow>
        <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.0] tracking-tight sm:text-6xl">Production stories, case studies & notes from the studio.</h1>
        <div className="mt-8 flex flex-wrap gap-2" role="tablist" aria-label="Filter articles">
          {cats.map((c) => (
            <button
              key={c}
              role="tab"
              aria-selected={cat === c}
              onClick={() => setCat(c)}
              data-testid={`journal-filter-${c.toLowerCase().replace(/[\s-]+/g, "-")}`}
              className={`rounded-full border px-4 py-2 text-xs font-mono uppercase tracking-[0.15em] transition-colors ${
                cat === c
                  ? "border-ink bg-ink text-cream dark:border-cream dark:bg-cream dark:text-ink"
                  : "border-border text-muted-foreground hover:border-ink hover:text-foreground dark:hover:border-cream"
              }`}
            >
              {c === "all" ? `All (${posts.length})` : c}
            </button>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        {loading ? (
          <p className="text-muted-foreground" data-testid="journal-loading">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-muted-foreground" data-testid="journal-empty">No articles yet.</p>
        ) : (
          <>
            {first && (
              <FadeUp>
                <Link to={`/journal/${first.slug}`} className="group grid gap-6 lg:grid-cols-2" data-testid={`journal-featured-${first.slug}`}>
                  <div className="overflow-hidden rounded-2xl border border-border">
                    <img src={first.image} alt={first.title} className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  </div>
                  <div className="flex flex-col justify-center">
                    <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                      <span className="text-orange">{first.category}</span> — {formatDate(first.published_at)}
                    </p>
                    <h2 className="mt-4 font-display text-3xl font-bold tracking-tight transition-colors group-hover:text-orange sm:text-4xl">{first.title}</h2>
                    <p className="mt-4 text-base leading-relaxed text-muted-foreground">{first.excerpt}</p>
                  </div>
                </Link>
              </FadeUp>
            )}
            <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {rest.map((j, i) => (
                <FadeUp key={j.id} delay={i * 0.08}>
                  <Link to={`/journal/${j.slug}`} className="group block" data-testid={`journal-card-${j.slug}`}>
                    <div className="overflow-hidden rounded-2xl border border-border">
                      <img src={j.image} alt={j.title} loading="lazy" className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.05]" />
                    </div>
                    <p className="mt-4 text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                      <span className="text-orange">{j.category}</span> — {formatDate(j.published_at)}
                    </p>
                    <h3 className="mt-2 font-display text-xl font-bold tracking-tight transition-colors group-hover:text-orange">{j.title}</h3>
                    <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-muted-foreground">{j.excerpt}</p>
                  </Link>
                </FadeUp>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
